import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
  useTheme,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import aiBg from "../Layout/HaulTruck.png";
import MineProdChart, { isMineProdChart, parseChartPayload } from "./MineProdChart";
import { useAuth } from "../../services/AuthContext";
import MenuIcon from "@mui/icons-material/Menu";
import ChatSidebar, { DEFAULT_DATE_FILTER, dateFilterToRange } from "./Chatsidebar";
import { API_BASE_URL } from "../../services/api";
import useSpeechToText from "../../hooks/useSpeechToText";


// Pull a MineProd chart envelope out of an SSE event payload (chart may be null).
const chartFromEvent = (evt) => {
  const candidates = [evt?.chart, evt?.data?.chart, evt?.data, evt?.payload?.chart, evt?.payload];
  for (const c of candidates) {
    if (isMineProdChart(c)) return c;
  }
  if (typeof evt?.text === "string") {
    try {
      const parsed = JSON.parse(evt.text);
      if (isMineProdChart(parsed)) return parsed;
      if (isMineProdChart(parsed?.chart)) return parsed.chart;
    } catch {
      /* not JSON */
    }
  }
  return null;
};

// Collect the raw text content of a markdown <pre><code> node (hast).
const rawTextFromPreNode = (node) => {
  const parts = [];
  const walk = (n) => {
    if (!n) return;
    if (typeof n.value === "string") parts.push(n.value);
    if (Array.isArray(n.children)) n.children.forEach(walk);
  };
  walk(node);
  return parts.join("");
};


const wavingDotsStyle = `
  @keyframes wave {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-5px); }
  }
  .thinking-dot {
    display: inline-block;
    animation: wave 1.2s ease-in-out infinite;
  }
  .thinking-dot:nth-child(2) { animation-delay: 0.15s; }
  .thinking-dot:nth-child(3) { animation-delay: 0.3s; }
  @keyframes slideInLine {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CHAT_ENDPOINTS = {
  operational: {
    sync: `${API_BASE_URL}/v1/ai/chat`,
    stream: `${API_BASE_URL}/v1/ai/chat`,
  },
  documents: {
    sync: `${API_BASE_URL}/v1/ai/documents/search`,
    stream: `${API_BASE_URL}/v1/ai/documents/search`,
  },
};

// UI tab name -> the `mode` value stored on chat sessions by the API.
const API_MODE = {
  operational: "operations",
  documents: "documents",
};

// Base path of DocumentUploadResource (adjust if its @Path differs).
const DOCUMENTS_API = `${API_BASE_URL}/v1/documents`;

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("operational");
  const modeRef = useRef(mode);
  modeRef.current = mode; // lets async callbacks tell whether the tab changed while they were running
  const { user, refresh, logout } = useAuth();
  const makeGreeting = (m = mode) => ({
    role: "assistant",
    content:
      m === "documents"
        ? `Hi ${user?.firstName || "there"}! Ask me a question about your uploaded documents and I'll answer from them, with sources.`
        : `Hi ${user?.firstName || "there"}! I'm **MineProd AI**. Ask me anything about plans, production, inspections, or haul cycles.`,
    ts: Date.now(),
  });

  const [messages, setMessages] = useState([
    makeGreeting(),
  ]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionQuery, setSessionQuery] = useState("");
  

  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [maximized, setMaximized] = useState(false);

  const sidebarEnabled = maximized; // chat history is available in both tabs when maximized
  
  const [dummyLines, setDummyLines] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [notice, setNotice] = useState(null);
  const copyTimerRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const chatEndRef = useRef(null);
  const panelRef = useRef(null);
  const messageListRef = useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [dateFilter, setDateFilter] = useState(DEFAULT_DATE_FILTER);
  
  // Render a ```mineprod-chart / ```json code block as an inline chart, in place
  // within the response. Non-chart code blocks fall back to a normal <pre>.
  const markdownComponents = useMemo(
    () => ({
      pre({ node, children, ...props }) {
        const chart = parseChartPayload(rawTextFromPreNode(node));
        if (chart) return <MineProdChart chart={chart} isDark={isDark} />;
        return <pre {...props}>{children}</pre>;
      },
    }),
    [isDark]
  );

  // Drag & resize state
  const [size, setSize] = useState({ width: 400, height: 560 });
  const [position, setPosition] = useState(null); // {x, y} top-left in px; null = default bottom-right anchor
  const [interacting, setInteracting] = useState(false);
  const dragState = useRef(null);
  const resizeState = useRef(null);

  const MIN_WIDTH = 320;
  const MIN_HEIGHT = 360;

  const ensurePosition = () => {
    if (position) return position;
    const rect = panelRef.current?.getBoundingClientRect();
    const pos = rect
      ? { x: rect.left, y: rect.top }
      : {
          x: window.innerWidth - size.width - 24,
          y: window.innerHeight - size.height - 24,
        };
    setPosition(pos);
    return pos;
  };

  const onDragMove = (e) => {
    const d = dragState.current;
    if (!d) return;
    let nx = d.origX + (e.clientX - d.startX);
    let ny = d.origY + (e.clientY - d.startY);
    nx = Math.max(0, Math.min(nx, window.innerWidth - size.width));
    ny = Math.max(0, Math.min(ny, window.innerHeight - size.height));
    setPosition({ x: nx, y: ny });
  };

  const onDragEnd = () => {
    dragState.current = null;
    setInteracting(false);
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
  };

  const onDragStart = (e) => {
    if (maximized) return;
    if (e.target.closest("button")) return; // let header buttons work
    e.preventDefault();
    const start = ensurePosition();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: start.x,
      origY: start.y,
    };
    setInteracting(true);
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  };

  const onResizeMove = (e) => {
    const r = resizeState.current;
    if (!r) return;
    let nw = r.origW + (e.clientX - r.startX);
    let nh = r.origH + (e.clientY - r.startY);
    nw = Math.max(MIN_WIDTH, Math.min(nw, window.innerWidth - r.origX));
    nh = Math.max(MIN_HEIGHT, Math.min(nh, window.innerHeight - r.origY));
    setSize({ width: nw, height: nh });
  };

  const onResizeEnd = () => {
    resizeState.current = null;
    setInteracting(false);
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", onResizeEnd);
  };

  const onResizeStart = (e) => {
    if (maximized) return;
    e.preventDefault();
    e.stopPropagation();
    const start = ensurePosition();
    resizeState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origW: size.width,
      origH: size.height,
      origX: start.x,
      origY: start.y,
    };
    setInteracting(true);
    window.addEventListener("mousemove", onResizeMove);
    window.addEventListener("mouseup", onResizeEnd);
  };

  const bubbleBg = theme.palette.primary.main;
  const textColor = isDark ? "#E5E7EB" : "#ffffff";
  const panelText = isDark ? "#E5E7EB" : theme.palette.text.primary;
  const dividerColor = isDark ? "#303640" : theme.palette.divider;

  // --- Speech-to-text (Amazon Transcribe streaming) -----------------------
  const appendTranscript = useCallback((text) => {
    const clean = text.trim();
    if (!clean) return;
    setInput((prev) => {
      if (!prev) return clean;
      return /\s$/.test(prev) ? prev + clean : `${prev} ${clean}`;
    });
  }, []);

  const stt = useSpeechToText({ onFinalText: appendTranscript });

  useEffect(() => {
    if (stt.error) {
      // Surface the problem in the composer; recording has already stopped.
      // eslint-disable-next-line no-console
      console.error("Speech-to-text error:", stt.error);
    }
  }, [stt.error]);


  useLayoutEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, dummyLines]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const formatTime = (ts) => {
    const d = new Date(ts);
    
    // Formats to "Oct 20, 2026"
    const date = d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    
    // Formats to "18:30" (24-hour)
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    
    return `${date} @ ${time}`;
  };

  const authedFetch = async (url, options = {}, { silent = false } = {}) => {
    const send = () => fetch(url, { ...options, credentials: "include" });
    let res = await send();
    if (res.status === 401) {
      if (silent) return res;                // background call: no refresh, no logout
      const user = await refresh();
      if (!user) {
        await logout();
        throw new Error("SESSION_EXPIRED");
      }
      res = await send();
    }
    return res;
  };

  const showNotice = (text) => {
    setNotice(text);
    clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 4000);
  };

  // Documents tab: fetch the sources behind an answer once its stream has finished.
  const loadCitations = async (id) => {
    try {
      const res = await authedFetch(`${API_BASE_URL}/v1/ai/messages/${id}/citations`, {}, { silent: true });
      if (!res.ok) return;
      const list = await res.json();
      if (!Array.isArray(list)) return;
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, citations: list } : m)));
    } catch (err) {
      window.console.error("Load sources failed:", err);
    }
  };

  // Download links are generated on demand (they expire after a few minutes).
  const openCitation = async (c) => {
    if (!c?.fileId) return;
    try {
      const res = await authedFetch(`${DOCUMENTS_API}/${c.fileId}/download-url`);
      if (res.status === 404) {
        showNotice("This document is no longer available.");
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url } = await res.json();
      if (!url) throw new Error("No download url returned");
      // The link is served as an attachment, so this downloads without leaving the page.
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      window.console.error("Open source failed:", err);
      showNotice("Could not open the document. Please try again.");
    }
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    if (stt.isRecording) stt.stop();
    setMessages((prev) => [...prev, { role: "user", content: trimmed, ts: Date.now() }]);
    setInput("");
    setLoading(true);

    let lineId = 0;
    let started = false; // has the assistant bubble been created yet?
    let messageId = null;

    const pushStatus = (text) =>
      setDummyLines((prev) => {
        const list = prev || [];
        if (list.length && list[list.length - 1].text === text) return list;
        return [...list, { id: lineId++, text }].slice(-3);
      });

    const appendDelta = (text) => {
      if (!started) {
        started = true;
        setDummyLines(null); // answer began: hide status box
        setMessages((prev) => [...prev, { id: messageId, role: "assistant", content: text, ts: Date.now() }]);
        return;
      }
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        updated[updated.length - 1] = { ...last, content: last.content + text, ts: Date.now() };
        return updated;
      });
    };

    // Attach a chart payload to the current assistant bubble (creating it if the
    // chart event arrives before any text delta).
    const attachChart = (chart) => {
      if (!isMineProdChart(chart)) return;
      if (!started) {
        started = true;
        setDummyLines(null);
        setMessages((prev) => [...prev, { id: messageId, role: "assistant", content: "", chart, ts: Date.now() }]);
        return;
      }
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        updated[updated.length - 1] = { ...last, chart, ts: Date.now() };
        return updated;
      });
    };

      const handleEvent = (evt) => {
        if (evt.type === "session") setCurrentSessionId(Number(evt.text));
        else if (evt.type === "meta") messageId = Number(evt.text);
        else if (evt.type === "status" && !started) pushStatus(evt.text);
        else if (evt.type === "delta" && evt.text) appendDelta(evt.text);
        else if (evt.type === "chart") attachChart(chartFromEvent(evt));
        else if (evt.type === "error") throw new Error(evt.text || "Stream error");
      };

      pushStatus(mode === "documents" ? "Searching documents..." : "Sending your question...");

      try {
        const response = await authedFetch(CHAT_ENDPOINTS[mode].sync, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
          credentials: "include",
          body: JSON.stringify({ question: trimmed, chatSessionId: currentSessionId }),
        });
        
        if (!response.ok || !response.body) throw new Error(`HTTP error: ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

          const frames = buffer.split("\n\n");
          buffer = frames.pop(); // keep incomplete trailing frame

          for (const frame of frames) {
            const data = frame
              .split("\n")
              .filter((l) => l.startsWith("data:"))
              .map((l) => l.slice(5).replace(/^ /, ""))
              .join("\n");
            if (!data) continue;
            try {
              handleEvent(JSON.parse(data));
            } catch (e) {
              if (e instanceof SyntaxError) continue; // ignore malformed frame
              throw e;
            }
          }
        }

        // Stream finished: load the sources the answer cited (documents tab only).
        if (mode === "documents" && messageId) await loadCitations(messageId);
      } catch (error) {
        setDummyLines(null);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Sorry, something went wrong. Please try again.", ts: Date.now() },
        ]);
      } finally {
        setDummyLines(null);
        setLoading(false);
        if (sidebarEnabled) loadSessions();
      }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = async (index, content) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = content;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedIndex(index);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedIndex(null), 1800);
    } catch {
      // clipboard unavailable; leave the icon unchanged
    }
  };

  const handleFeedback = async (index, value) => {
    const msg = messages[index];
    if (!msg?.id) return; // greeting / error messages have no server id
    const previous = feedback[index] ?? null;
    const next = previous === value ? null : value; // clicking the same thumb again removes the rating
    setFeedback((prev) => ({ ...prev, [index]: next })); // optimistic update

    try {
      const res = await authedFetch(`${API_BASE_URL}/v1/ai/messages/${msg.id}/feedback`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ rating: next }), // "like" | "dislike" | null
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      setFeedback((prev) => ({ ...prev, [index]: previous })); // roll back
    }
  };
  
  const loadSessions = async (q = sessionQuery, f = dateFilter) => {
    const requestedMode = mode;
    setSessionsLoading(true);
    try {
      const { from, to } = dateFilterToRange(f);
      const params = new URLSearchParams({ limit: "100", mode: API_MODE[requestedMode] });
      if (q) params.set("q", q);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const res = await authedFetch(`${API_BASE_URL}/v1/ai/sessions?${params.toString()}`);
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      // The user may have switched tabs while this was loading; drop stale results.
      if (modeRef.current === requestedMode) setSessions(data);
    } catch (err) {
      window.console.error("Load chats failed:", err);
    } finally {
      if (modeRef.current === requestedMode) setSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !sidebarEnabled) return;
    const t = setTimeout(() => loadSessions(sessionQuery, dateFilter), sessionQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [open, sidebarEnabled, sessionQuery, dateFilter, mode]);

  useEffect(() => { setSidebarOpen(maximized); }, [maximized]);

  const toggleMaximize = () => {
    if (!maximized) {
      setMaximized(true);
      return;
    }
    setMaximized(false);
    setSessionQuery("");
    setDateFilter(DEFAULT_DATE_FILTER);
    resetChat(); // new chat in the small window
  };

  const resetChat = (m = mode) => {
    setCurrentSessionId(null);
    setMessages([makeGreeting(m)]);
    setFeedback({});
  };

  // Each tab has its own chats: switching starts a fresh chat and reloads that tab's history.
  const handleModeChange = (next) => {
    if (!next || next === mode || loading) return;
    setMode(next);
    resetChat(next);
    setSessions([]);
    setSessionQuery("");
    setDateFilter(DEFAULT_DATE_FILTER);
    setSessionsLoading(false);
    setNotice(null);
  };

  const startNewChat = () => {
    if (loading) return;
    resetChat();
    if (!maximized) setSidebarOpen(false);
  };

  const openSession = async (id) => {
    if (loading || id === currentSessionId) return;
    try {
      const res = await authedFetch(`${API_BASE_URL}/v1/ai/sessions/${id}/messages`);
      if (!res.ok) throw new Error(res.status);
      const rows = await res.json();
      const msgs = [];
      const fb = {};
      rows.forEach((r) => {
        msgs.push({ role: "user", content: r.question, ts: Date.parse(r.createdOn) });
        msgs.push({
          id: r.id,
          role: "assistant",
          content: r.response || "_No response was recorded for this question._",
          ts: Date.parse(r.completedOn || r.createdOn),
          ...(Array.isArray(r.citations) && r.citations.length ? { citations: r.citations } : {}),
        });
        if (r.rating) fb[msgs.length - 1] = r.rating; // keyed by the assistant message index
      });
      setMessages(msgs.length ? msgs : [makeGreeting()]);
      setFeedback(fb);
      setCurrentSessionId(id);
      if (!maximized) setSidebarOpen(false);
    } catch (err) {
      window.console.error("Open chat failed:", err);
    }
  };

  const updateSession = async (id, patch) => {
    const res = await authedFetch(`${API_BASE_URL}/v1/ai/sessions/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(res.status);
    await loadSessions();
  };

  const deleteSession = async (id) => {
    const res = await authedFetch(`${API_BASE_URL}/v1/ai/sessions/${id}`, { method: "DELETE" });
    if (!res.ok) throw new Error(res.status);
    if (id === currentSessionId) resetChat();
    await loadSessions();
  };

  useEffect(
    () => () => {
      clearTimeout(copyTimerRef.current);
      clearTimeout(noticeTimerRef.current);
    },
    []
  );

  return (
    <>
      <style>{wavingDotsStyle}</style>
      {/* Floating Bubble */}
      {!open && (
        <Tooltip title="Ask AI" placement="left">
          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: bubbleBg,
              color: textColor,
              boxShadow: 4,
              cursor: "pointer",
              zIndex: 2000,
              backgroundColor: isDark ? "#181A1B" : theme.palette.background.paper,
              // backgroundImage: isDark ? "none" : `url(${aiBg})`,
              backgroundImage: `url(${aiBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundBlendMode: "soft-lighten",
            }}
            onClick={() => setOpen(true)}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                color: textColor,
                textShadow: "0px 0px 4px rgba(0,0,0,0.6)",
              }}
            >
              <AutoAwesomeOutlinedIcon fontSize="medium" />
            </Box>
          </Box>
        </Tooltip>
      )}

      {/* Floating Assistant Panel */}
      {open && (
        <Paper
          ref={panelRef}
          elevation={6}
          sx={{
            position: "fixed",
            ...(maximized
              ? { bottom: "2vh", right: "2vw", top: "auto", left: "auto", width: "96vw", height: "90vh" }
              : position
              ? { left: position.x, top: position.y, bottom: "auto", right: "auto", width: size.width, height: size.height }
              : { bottom: 24, right: 24, top: "auto", left: "auto", width: size.width, height: size.height }),
            borderRadius: 3,
            transition: interacting ? "none" : "all 0.25s ease",
            userSelect: interacting ? "none" : "auto",
            display: "flex",
            flexDirection: "column",
            zIndex: 2000,
            backgroundImage: `url(${aiBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundBlendMode: "overlay",
            color: panelText,
            fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
            border: isDark ? "1px solid #303640" : "1px solid rgba(96, 165, 250, 0.22)",
            boxShadow:
              !isDark
                ? "0 18px 36px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(148, 163, 184, 0.16)"
                : "0 20px 48px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(96, 165, 250, 0.06)",
          }}
        >
          {/* Header */}
          <Box
            onMouseDown={onDragStart}
            sx={{
              p: 2,
              borderBottom: `1px solid ${dividerColor}`,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              cursor: maximized ? "default" : "move",
              backdropFilter: isDark ? "none" : "blur(6px)",
              backgroundColor:
                !isDark
                  ? "rgba(255,255,255,0.7)"
                  : "#181A1B",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {sidebarEnabled && (
                  <Tooltip title={sidebarOpen ? "Hide chats" : "Chats"}>
                    <IconButton size="small" aria-label="Toggle chat list" onClick={() => setSidebarOpen((o) => !o)}
                      sx={{ color: isDark ? "#9CA3AF" : "inherit" }}>
                      <MenuIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography variant="subtitle1" fontWeight={600} color={isDark ? "#F3F4F6" : "inherit"}>
                  MineProd AI <AutoAwesomeOutlinedIcon fontSize="medium" />
                </Typography>
              </Box>
              <Box sx={{ "& .MuiIconButton-root": { color: isDark ? "#9CA3AF" : "inherit", "&:hover": { color: isDark ? "#60A5FA" : "inherit", backgroundColor: isDark ? "rgba(96,165,250,0.1)" : undefined } } }}>
                <IconButton
                    size="small"
                    aria-label={maximized ? "Restore window" : "Maximize window"}
                    disabled={loading}
                    onClick={toggleMaximize}
                  >
                  {maximized ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={() => setOpen(false)}>
                  {/* <MinimizeIcon fontSize="small" /> */}
                </IconButton>
                <IconButton size="small" onClick={() => setOpen(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={mode}
              onChange={(_, next) => handleModeChange(next)}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  py: 0.4,
                  borderColor: isDark ? "#303640" : "rgba(148, 163, 184, 0.22)",
                  color: isDark ? "#9CA3AF" : theme.palette.text.secondary,
                  transition: "all 0.2s ease",
                },
                "& .MuiToggleButton-root.Mui-selected": {
                  backgroundColor: isDark ? "rgba(29,78,216,0.2)" : "rgba(59,130,246,0.12)",
                  color: isDark ? "#BFDBFE" : theme.palette.primary.main,
                  boxShadow: isDark ? "inset 0 0 0 1px rgba(96,165,250,0.4)" : "none",
                },
                "& .MuiToggleButton-root:focus-visible": {
                  outline: "2px solid #60A5FA",
                  outlineOffset: 2,
                },
              }}
            >
              <ToggleButton value="operational" disabled={loading}>
                Operational
              </ToggleButton>
              <ToggleButton value="documents" disabled={loading}>
                Documents
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, display: "flex", position: "relative" }}>
            {sidebarEnabled && sidebarOpen && (
            <Box sx={{ width: 260, flexShrink: 0, borderRight: `1px solid ${dividerColor}` }}>
              <ChatSidebar
                sessions={sessions}
                loading={sessionsLoading}
                query={sessionQuery}
                onQueryChange={setSessionQuery}
                currentId={currentSessionId}
                disabled={loading}
                isDark={isDark}
                onNew={startNewChat}
                onSelect={openSession}
                onPin={(id, pinned) => updateSession(id, { pinned })}
                onRename={(id, title) => updateSession(id, { title })}
                onDelete={deleteSession}
                dateFilter={dateFilter}
                onDateFilterChange={setDateFilter}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {/* Message List */}
          <Box
            ref={messageListRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              backdropFilter: isDark ? "none" : "blur(4px)",
              backgroundColor:
                !isDark
                  ? "rgba(255,255,255,0.4)"
                  : "#121212",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: !isDark ? "rgba(0,0,0,0.18)" : "#4B5563",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: !isDark ? "rgba(0,0,0,0.32)" : "#60A5FA",
              },
              scrollbarWidth: "thin",
              scrollbarColor: !isDark ? "rgba(0,0,0,0.18) transparent" : "#4B5563 transparent",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                p: 2,
              }}
            >
            {messages.map((msg, i) => {
              const isUser = msg.role === "user";
              return (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      maxWidth: isUser ? "78%" : "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                      overflowX: isUser ? "visible" : "auto",
                      px: 1.5,
                      py: 1,
                      fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      lineHeight: 1.6,
                      // iMessage-style pill shape
                      borderRadius: isUser
                        ? "18px 18px 4px 18px"
                        : "18px 18px 18px 4px",
                      backgroundColor: isUser
                        ? "#1D4ED8"
                        : !isDark
                        ? "rgba(255,255,255,0.82)"
                        : "#1E2229",
                      background: isUser
                        ? "#1D4ED8"
                        : !isDark
                        ? "rgba(255,255,255,0.82)"
                        : "#1E2229",
                      color: isUser
                        ? "#F3F4F6"
                        : !isDark
                        ? "#1f2937"
                        : "#E5E7EB",
                      border: !isDark
                        ? "1px solid rgba(148,163,184,0.22)"
                        : isUser
                        ? "1px solid rgba(96,165,250,0.35)"
                        : "1px solid transparent",
                      boxShadow: isUser
                        ? "0 4px 14px rgba(29,78,216,0.2)"
                        : !isDark
                        ? "0 0 0 1px rgba(148,163,184,0.08), 0 6px 18px rgba(15,23,42,0.06)"
                        : "0 3px 12px rgba(0,0,0,0.2)",
                      "& p": { margin: 0, "& + p": { mt: 1 } },
                      "& ul, & ol": { pl: 2.25, my: 0.75 },
                      "& li": { mb: 0.35 },
                      "& h1, & h2, & h3, & h4": {
                        color: isDark ? "#F3F4F6" : "inherit",
                        fontWeight: 600,
                        lineHeight: 1.35,
                        marginTop: "0.8em",
                        marginBottom: "0.45em",
                      },
                      "& strong": {
                        fontWeight: 600,
                        color: isUser ? "#FFFFFF" : !isDark ? "#0f172a" : "#F3F4F6",
                      },
                      "& a": {
                        color: isDark ? "#60A5FA" : theme.palette.primary.main,
                        fontWeight: 500,
                        textDecorationColor: isDark ? "rgba(96,165,250,0.55)" : "currentColor",
                        textUnderlineOffset: "2px",
                        "&:hover": { color: isDark ? "#93C5FD" : theme.palette.primary.dark },
                        "&:focus-visible": {
                          outline: "2px solid #60A5FA",
                          outlineOffset: 2,
                          borderRadius: "2px",
                        },
                      },
                      "& code": {
                        backgroundColor: isUser
                          ? "rgba(255,255,255,0.2)"
                          : isDark ? "#2A2F3A" : "rgba(0,0,0,0.08)",
                        borderRadius: "4px",
                        px: 0.5,
                        fontFamily: "monospace",
                        fontSize: "0.78rem",
                      },
                      "& pre": {
                        backgroundColor: isUser
                          ? "rgba(255,255,255,0.15)"
                          : isDark ? "#181A1B" : "rgba(0,0,0,0.06)",
                        borderRadius: "8px",
                        p: 1,
                        overflowX: "auto",
                        "& code": { backgroundColor: "transparent" },
                      },
                      "& table": {
                        borderCollapse: "collapse",
                        width: "max-content",
                        minWidth: "100%",
                        fontSize: "0.75rem",
                        marginTop: 1,
                        marginBottom: 1,
                      },
                      "& th, & td": {
                        border: `1px solid ${isDark ? "#3B424E" : "rgba(148, 163, 184, 0.7)"}`,
                        padding: "6px 8px",
                        textAlign: "left",
                        verticalAlign: "top",
                      },
                      "& th": {
                        backgroundColor: isUser ? "rgba(255,255,255,0.12)" : isDark ? "#262A34" : "rgba(148,163,184,0.16)",
                        color: isDark ? "#F3F4F6" : "inherit",
                        fontWeight: 600,
                      },
                    }}
                  >
                    {msg.role === "assistant" ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {msg.content}
                      </ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                    {msg.role === "assistant" && isMineProdChart(msg.chart) && (
                      <MineProdChart chart={msg.chart} isDark={isDark} />
                    )}
                  </Box>
                  {!isUser && Array.isArray(msg.citations) && msg.citations.length > 0 && (
                    <Box sx={{ mt: 0.75, ml: 0.5, maxWidth: "100%" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mb: 0.4,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          letterSpacing: 0.4,
                          textTransform: "uppercase",
                          color: isDark ? "#9CA3AF" : "text.secondary",
                        }}
                      >
                        Sources
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {msg.citations.map((c) => (
                          <Tooltip key={c.index} title={c.snippet || ""} placement="top" enterDelay={400}>
                            <Chip
                              size="small"
                              icon={<DescriptionOutlinedIcon />}
                              label={`[${c.index}] ${c.filename || "Document"}${c.page ? ` · p.${c.page}` : ""}`}
                              clickable={!!c.fileId}
                              onClick={c.fileId ? () => openCitation(c) : undefined}
                              sx={{
                                maxWidth: "100%",
                                height: 24,
                                fontSize: "0.72rem",
                                color: isDark ? "#BFDBFE" : theme.palette.primary.main,
                                bgcolor: isDark ? "#262A34" : "rgba(148,163,184,0.16)",
                                "& .MuiChip-icon": { color: "inherit", fontSize: 15 },
                                "&:hover": {
                                  bgcolor: isDark ? "rgba(96,165,250,0.18)" : "rgba(59,130,246,0.18)",
                                },
                                "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 2 },
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {isUser && msg.ts && (
                    <Tooltip title={new Date(msg.ts).toLocaleString()} placement="left">
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          mr: 0.5,
                          fontSize: "0.68rem",
                          color: isDark ? "#9CA3AF" : "text.secondary",
                        }}
                      >
                        {formatTime(msg.ts)}
                      </Typography>
                    </Tooltip>
                  )}
                  {!isUser && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        mt: 0.5,
                        ml: 0.5,
                        "& .MuiIconButton-root": {
                          color: isDark ? "#9CA3AF" : "rgba(0,0,0,0.5)",
                          p: 0.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            color: isDark ? "#60A5FA" : theme.palette.primary.main,
                            backgroundColor: isDark ? "rgba(96,165,250,0.12)" : "rgba(29,78,216,0.08)",
                          },
                          "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 2 },
                        },
                      }}
                    >
                      <Tooltip title={copiedIndex === i ? "Copied" : "Copy"} placement="top">
                        <IconButton size="small" aria-label="Copy response" onClick={() => handleCopy(i, msg.content)}>
                          {copiedIndex === i ? (
                            <CheckIcon sx={{ fontSize: 15, color: isDark ? "#34D399" : "#059669" }} />
                          ) : (
                            <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      {msg.id && (
                      <>
                        <Tooltip title="Good response" placement="top">
                          <IconButton
                            size="small"
                            aria-label="Good response"
                            aria-pressed={feedback[i] === "like"}
                            onClick={() => handleFeedback(i, "like")}
                          >
                            {feedback[i] === "like" ? (
                              <ThumbUpIcon sx={{ fontSize: 15, color: isDark ? "#34D399" : "#059669" }} />
                            ) : (
                              <ThumbUpOutlinedIcon sx={{ fontSize: 15 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Bad response" placement="top">
                          <IconButton
                            size="small"
                            aria-label="Bad response"
                            aria-pressed={feedback[i] === "dislike"}
                            onClick={() => handleFeedback(i, "dislike")}
                          >
                            {feedback[i] === "dislike" ? (
                              <ThumbDownIcon sx={{ fontSize: 15, color: isDark ? "#F87171" : "#DC2626" }} />
                            ) : (
                              <ThumbDownOutlinedIcon sx={{ fontSize: 15 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </>
                      )}
                      {msg.ts && (
                        <Tooltip title={new Date(msg.ts).toLocaleString()} placement="top">
                          <Typography
                            variant="caption"
                            sx={{
                              ml: 1,
                              fontSize: "0.68rem",
                              color: isDark ? "#9CA3AF" : "text.secondary",
                              cursor: "default",
                            }}
                          >
                            {formatTime(msg.ts)}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
            {dummyLines !== null && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Box
                  sx={{
                    maxWidth: "88%",
                    px: 1.5,
                    py: 1.1,
                    borderRadius: "18px 18px 18px 4px",
                    backgroundColor: !isDark ? "#e9e9eb" : "#1E2229",
                    color: !isDark ? "#1c1c1e" : "#9CA3AF",
                    boxShadow: !isDark ? "0 1px 2px rgba(0,0,0,0.15)" : "0 3px 12px rgba(0,0,0,0.2)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.6,
                  }}
                >
                  {dummyLines.map((line, i) => {
                      const isLast = i === dummyLines.length - 1;
                      const opacities = [0.3, 0.55, 1];
                      const opacity = opacities[Math.max(0, i - (dummyLines.length - opacities.length))] ?? 1;
                      return (
                        <Box key={line.id} sx={{ display: "flex", alignItems: "center", gap: 0.9, opacity, animation: "slideInLine 0.25s ease-out" }}>
                          {isLast ? (
                            <Box component="span" sx={{ width: 11, height: 11, border: "1.5px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", flexShrink: 0, animation: "spin 0.75s linear infinite" }} />
                          ) : (
                            <Box component="span" sx={{ width: 11, height: 11, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                                <path d="M2 5.5l2.5 2.5L9 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </Box>
                          )}
                          <Typography sx={{ fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif', fontSize: "0.82rem", fontWeight: 500, lineHeight: 1.5 }}>{line.text}</Typography>
                        </Box>
                      );
                  })}
                </Box>
              </Box>
            )}
            <div ref={chatEndRef} />
            </Box>
          </Box>

          {/* Input Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `1px solid ${dividerColor}`,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              backdropFilter: isDark ? "none" : "blur(6px)",
              backgroundColor:
                !isDark
                  ? "rgba(255,255,255,0.7)"
                  : "#181A1B",
            }}
          >
            {notice && (
              <Typography
                role="status"
                variant="caption"
                sx={{ px: 0.5, color: isDark ? "#FCA5A5" : "#B91C1C" }}
              >
                {notice}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder={
                stt.isRecording
                  ? "Listening… speak now"
                  : mode === "documents"
                  ? "Ask about your documents..."
                  : "Ask me anything..."
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: !isDark ? "rgba(255,255,255,0.7)" : "#2A2F3A",
                  color: isDark ? "#E5E7EB" : "inherit",
                  fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  lineHeight: 1.5,
                  transition: "all 0.2s ease",
                  "& fieldset": {
                    borderColor: isDark ? "#414854" : "rgba(148,163,184,0.26)",
                  },
                  "&:hover fieldset": {
                    borderColor: isDark ? "#60A5FA" : "rgba(96,165,250,0.45)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#60A5FA",
                    boxShadow: "0 0 0 3px rgba(96,165,250,0.14)",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: isDark ? "#9CA3AF" : "inherit",
                  opacity: 1,
                },
              }}
            />
            {stt.isSupported && (
              <Tooltip title={stt.isRecording ? "Stop voice input" : "Speak your question"}>
                <span>
                  <IconButton
                    onClick={stt.toggle}
                    disabled={loading || stt.isConnecting}
                    aria-label={stt.isRecording ? "Stop voice input" : "Start voice input"}
                    sx={{
                      mb: 0.25,
                      color: stt.isRecording ? "#EF4444" : isDark ? "#9CA3AF" : undefined,
                      "@keyframes sttPulse": {
                        "0%": { boxShadow: "0 0 0 0 rgba(239,68,68,0.45)" },
                        "70%": { boxShadow: "0 0 0 8px rgba(239,68,68,0)" },
                        "100%": { boxShadow: "0 0 0 0 rgba(239,68,68,0)" },
                      },
                      animation: stt.isRecording ? "sttPulse 1.5s infinite" : "none",
                      "&:hover": { backgroundColor: isDark ? "rgba(96,165,250,0.12)" : undefined },
                      "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 2 },
                    }}
                  >
                    {stt.isConnecting ? (
                      <CircularProgress size={20} thickness={5} />
                    ) : stt.isRecording ? (
                      <StopIcon />
                    ) : (
                      <MicIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!input.trim() || loading}
              sx={{
                mb: 0.25,
                color: isDark ? "#60A5FA" : undefined,
                "&:hover": { backgroundColor: isDark ? "rgba(96,165,250,0.12)" : undefined },
                "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 2 },
                "&.Mui-disabled": { color: isDark ? "#6B7280" : undefined },
              }}
            >
              <SendIcon />
            </IconButton>
            </Box>
            {(stt.isRecording || stt.interimText || stt.error) && (
              <Typography
                variant="caption"
                sx={{
                  px: 0.5,
                  minHeight: 16,
                  color: stt.error
                    ? "#EF4444"
                    : isDark
                    ? "#9CA3AF"
                    : theme.palette.text.secondary,
                  fontStyle: stt.interimText ? "italic" : "normal",
                }}
              >
                {stt.error
                  ? stt.error.message || "Speech-to-text failed. Please try again."
                  : stt.interimText
                  ? `“${stt.interimText}”`
                  : "Listening…"}
              </Typography>
            )}
          </Box>
          </Box>
        </Box>
          

          {/* Resize handle (bottom-right) */}
          {!maximized && (
            <Box
              onMouseDown={onResizeStart}
              sx={{
                position: "absolute",
                bottom: 2,
                right: 2,
                width: 18,
                height: 18,
                cursor: "nwse-resize",
                zIndex: 3,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                color: theme.palette.mode === "light" ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.4)",
                "&:hover": {
                  color: theme.palette.mode === "light" ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.7)",
                },
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M11 1L1 11M11 5L5 11M11 9L9 11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </Box>
          )}
        </Paper>
      )}
    </>
  );
}